import { ReactNode } from 'react'
import { RiShareBoxFill } from 'react-icons/ri'
import { classNames, isValidUrl } from 'utils'
import { Tabs } from '../tabs'
import { PageHeaderProps } from './types'

const computeSubtitle = (
	subtitle: ReactNode | undefined,
	tagline: string | undefined,
	tags: Array<{ icon?: any; label?: string | null }> | undefined
): ReactNode => {
	if (subtitle) return subtitle
	const tagLabels = (tags || [])
		.map((t) => t?.label)
		.filter((l): l is string => !!l)
	if (tagline && tagLabels.length > 0) return `${tagline} ${tagLabels.join(', ')}`
	if (tagline) return tagline
	if (tagLabels.length > 0) return tagLabels.join(', ')
	return null
}

export const PageHeader = (props: PageHeaderProps) => {
	const {
		title,
		action,
		tags,
		subtitle,
		tabs,
		currentTab,
		tabsActions,
		info,
		tagline,
		titleBadge,
		actionPlacement = 'bottom',
		editAction,
		editActionPlacement = 'top',
		notices,
		noPadding,
		variant = 'inline'
	} = props

	const computedSubtitle = computeSubtitle(subtitle, tagline, tags)
	const isStacked = variant === 'stacked'

	return (
		<div className={classNames('flex flex-col')}>
			<div className="w-full flex flex-col tablet:flex-row tablet:justify-between tablet:items-center gap-2 tablet:gap-4">
				<div
					className={classNames(
						'flex flex-1 min-w-0',
						isStacked ? 'flex-col gap-2' : 'items-center gap-3',
						!noPadding ? 'px-4' : ''
					)}
				>
					<div className="flex items-center gap-3 min-w-0">
						<h2
							className="!my-0 font-grotesk truncate shrink-0 max-w-[45rem]"
							style={{
								color: 'rgb(var(--cg-text-primary))',
								fontSize: isStacked ? '24px' : '20px',
								lineHeight: isStacked ? '30px' : '26px',
								fontWeight: isStacked ? 500 : 600
							}}
						>
							{title}
						</h2>
						{title && isValidUrl(title) && (
							<a
								href={`//${title}`}
								target="_blank"
								className="pl-1 text-primary-500"
							>
								<RiShareBoxFill />
							</a>
						)}
					</div>
					{computedSubtitle && (
						<>
							{!isStacked && (
								<span
									aria-hidden
									className="shrink-0 select-none"
									style={{
										width: '1px',
										height: '17px',
										backgroundColor: 'rgb(var(--cg-border))'
									}}
								/>
							)}
							<span
								className="font-grotesk font-normal truncate min-w-0"
								style={{
									color: 'rgb(var(--cg-text-secondary))',
									fontSize: '14px',
									lineHeight: '18px'
								}}
							>
								{computedSubtitle}
							</span>
						</>
					)}
					{titleBadge && <span className="shrink-0">{titleBadge}</span>}
				</div>
				{editActionPlacement === 'top' && editAction && (
					<div
						className={classNames(
							'flex justify-end items-center flex-shrink-0',
							!noPadding ? 'px-4' : ''
						)}
					>
						<div className="flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6 w-full tablet:w-auto">
							{editAction}
						</div>
					</div>
				)}
				{actionPlacement === 'top' && action && (
					<div
						className={classNames(
							'flex justify-end items-center flex-shrink-0',
							!noPadding ? 'px-4' : ''
						)}
					>
						<div className="flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6 w-full tablet:w-auto">
							{action}
						</div>
					</div>
				)}
			</div>
			{notices && <div>{notices}</div>}
			{actionPlacement === 'bottom' && action && (
				<div
					className={classNames(
						'w-full flex min-w-0',
						!noPadding ? 'px-4' : ''
					)}
				>
					<div className="w-full flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
						{action}
					</div>
				</div>
			)}
			{(tabs && currentTab) || tabsActions || info ? (
				<div
					className={classNames(
						'flex gap-2 items-center justify-between',
						!noPadding ? 'px-4' : ''
					)}
				>
					<div className="w-full">
						{info && <div className="flex-1 flex-wrap">{info}</div>}
						{tabs && currentTab && (
							<div className="flex-1 flex-wrap">
								<Tabs current={currentTab} tabs={tabs} />
							</div>
						)}
					</div>
					{tabsActions && (
						<div className="w-full flex justify-end">{tabsActions}</div>
					)}
				</div>
			) : (
				<></>
			)}
		</div>
	)
}
